/**
 * プッシュ通知許可ボタンコンポーネント
 * ユーザーがブラウザプッシュ通知を有効化できるシンプルなボタン
 */
import React from 'react';
import { Button, message, Tooltip, Badge } from 'antd';
import { BellOutlined, BellFilled, LoadingOutlined } from '@ant-design/icons';
import { usePushNotification } from './PushNotificationProvider';

const PushNotificationButton = ({ 
  type = 'default', 
  size = 'middle',
  showText = true,
  className = ''
}) => {
  const {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
    requestPermission
  } = usePushNotification();

  // プッシュ通知非対応ブラウザの場合
  if (!isSupported) {
    return showText ? (
      <Tooltip title="お使いのブラウザはプッシュ通知に対応していません">
        <Button 
          type={type} 
          size={size}
          disabled
          icon={<BellOutlined />}
          className={className}
        >
          通知非対応
        </Button>
      </Tooltip>
    ) : null;
  }

  // 許可が拒否されている場合
  if (permission === 'denied') {
    return showText ? (
      <Tooltip title="ブラウザの設定からプッシュ通知を許可してください">
        <Button 
          type={type} 
          size={size}
          disabled
          icon={<BellOutlined />}
          className={className}
        >
          通知をブロック中
        </Button>
      </Tooltip>
    ) : (
      <Tooltip title="ブラウザの設定からプッシュ通知を許可してください">
        <Button 
          type={type} 
          size={size}
          disabled
          icon={<BellOutlined />}
          className={className}
        />
      </Tooltip>
    );
  }

  const handleClick = async () => {
    if (isSubscribed) {
      // 登録解除（成功メッセージはProviderで表示）
      const success = await unsubscribe();
      if (!success) {
        message.error('通知解除に失敗しました');
      }
    } else if (permission === 'granted') {
      // 許可済みなので登録
      const success = await subscribe();
      if (!success) {
        message.error('通知登録に失敗しました');
      }
    } else {
      // 許可を求めてから登録
      const granted = await requestPermission();
      if (granted) {
        const success = await subscribe();
        if (!success) {
          message.error('通知登録に失敗しました');
        }
      } else {
        message.warning('通知の許可が拒否されました');
      }
    }
  };

  // ローディング中
  if (isLoading) {
    return (
      <Button 
        type={type} 
        size={size}
        disabled
        icon={<LoadingOutlined />}
        className={className}
      >
        {showText && '処理中...'}
      </Button>
    );
  }

  if (isSubscribed) {
    return (
      <Tooltip title={<span>リサーチ・構成案・記事生成の完了時に通知します<br/><span style={{color: '#faad14', fontSize: 12}}>※ブラウザやPCの設定によっては通知されない場合があります</span></span>}>
        <Badge dot color="green" offset={[-5, 5]}>
          <Button 
            type={type} 
            size={size}
            icon={<BellFilled style={{ color: '#52c41a' }} />}
            onClick={handleClick}
            className={className}
          >
            {showText && '通知オン'}
          </Button>
        </Badge>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={<span>リサーチ・構成案・記事生成の完了時に通知します<br/><span style={{color: '#faad14', fontSize: 12}}>※ブラウザやPCの設定によっては通知されない場合があります</span></span>}>
      <Button 
        type={type} 
        size={size}
        icon={<BellOutlined />}
        onClick={handleClick}
        className={className}
      >
        {showText && '通知を有効化'}
      </Button>
    </Tooltip>
  );
};

export default PushNotificationButton;
